import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Link, useSearchParams } from "react-router-dom"

import { ActionIcon, Anchor, Badge, Table, Card, CopyButton, Container, ScrollArea, Group, Skeleton, Pagination, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { GET_ADMIN_PROJECTS } from '../../../graphql/queries';
import { Error } from '../../../Components/Error';
import { NetworkLogo } from '../../../Components/NetworkLogo';
import { CountryFlag } from '../../../Components/CountryFlag';
import { IconCopy, IconCheck, IconChartLine, IconSearch } from "@tabler/icons";


export function AdminProjectPage() {
  const perPage = 25;

  const [ params, setParams ] = useSearchParams();
  const page = parseInt(params.get("page") || "1");
  const token = params.get("token") || "";

  const [ tokenInput, setTokenInput ] = useState(token);
  const [ debouncedToken ] = useDebouncedValue(tokenInput, 400);

  useEffect(() => {
    if (debouncedToken === token) {
      return;
    }
    // filter change resets pagination to the first page
    setParams(debouncedToken ? { token: debouncedToken } : {});
  }, [debouncedToken, token, setParams]);

  const { loading, error, data } = useQuery(GET_ADMIN_PROJECTS, {
    variables: {
      offset: (page - 1) * perPage,
      limit: perPage,
      token: token || null
    }
  });
  let rows = null;

  const setPage = (page: number) => {
    setParams(token ? { page: page.toString(), token } : { page: page.toString() });
  }

  if (data) {
    rows = data.adminProjects.list.map((row: any) => {
      return (
        <tr key={row.id}>
          <td>{row.name}</td>
          <td>
            {row.user ? (
              <>
                <Anchor component={Link} to={`/admin/users/${row.userId}/view`}>
                  {row.user.email}
                </Anchor>
                {row.user.fullName && (
                  <>
                    ({row.user.fullName})
                  </>
                )}
                <div>
                  {row.userId}
                </div>
                {row.latestUsedIp && (
                  <div>
                    Last used IP: <CountryFlag code={row.latestUsedIpCountry} /> {row.latestUsedIp}
                  </div>
                )}
              </>
            ) : (
              <Anchor component={Link} to={`/admin/users/${row.userId}/view`}>
                {row.userId}
              </Anchor>
            )}
          </td>
          <td>
            <div>
              <Badge color={"green"}>{row.plan.slug}</Badge> {row.plan.requestsPerMonth.toLocaleString()} reqs/month.
            </div>
            <div>
              <Badge color={row.status === "active" ? "green" : "red"}>{row.status}</Badge>
            </div>
          </td>
          <td>
            <NetworkLogo network={row.network.slug} size={20} /><Badge color={"blue"} title={row.network.title}>{row.network.slug}</Badge>
          </td>
          <td>
            {row.currentStats.total}
          </td>
          <td>
            <Group spacing={0}>
              <ActionIcon
                component={Link}
                to={`/admin/users/${row.userId}/view`}
                color="blue"
                title="Open user page"
              >
                <IconChartLine size="1rem" stroke={1.5} />
              </ActionIcon>
              <CopyButton value={row.token}>
                {({ copied, copy }) => (
                  <ActionIcon color={copied ? 'teal' : 'gray'} onClick={copy}>
                  {copied ? <IconCheck size="1rem" /> : <IconCopy size="1rem" />}
                  </ActionIcon>
                )}
              </CopyButton>
            </Group>
          </td>
        </tr>
      );
    });
  }
  return (
    <>
      <Group align="flex-end">
        <h2 style={{ flex: 1 }} >Projects{data ? `, total ${data.adminProjects.count}` : ''}</h2>
        <TextInput
          my={20}
          icon={<IconSearch size="1rem" />}
          placeholder="Filter by token"
          value={tokenInput}
          onChange={(event) => setTokenInput(event.currentTarget.value)}
        />
      </Group>

      { loading ? (
        <>
          <Skeleton height={8} mt={6} radius="xl" />
          <Skeleton height={8} mt={6} radius="xl" />
          <Skeleton height={8} mt={6} radius="xl" />
        </>
      ) : (
        <>
        { error ? (
          <Error text={error?.toString()} />
        ) : (
          <>
            <Container>
              { rows && rows.length ? (
                <ScrollArea type="auto">
                  <Table
                    sx={(theme) => ({
                      minWidth: 800,
                      [theme.fn.smallerThan('sm')]: {
                        minWidth: 600,
                      },
                    })}
                    verticalSpacing="xs"
                  >
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>User</th>
                        <th>Plan/Status</th>
                        <th>Network</th>
                        <th>Used stats</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>{rows}</tbody>
                  </Table>
                </ScrollArea>
              ) : (
                <Card>{token ? `No projects match token "${token}".` : 'No projects. Please add one to get credentials.'}</Card>
              )}
              <Pagination value={page} total={Math.ceil(data.adminProjects.count / perPage)} onChange={setPage} />
            </Container>
          </>
        )}
        </>
      )}
    </>
  );
}